import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminComment, type AdminNews } from "@/modules/news/admin/types";

export type CommentList = Paginate<AdminComment<true, true>>;

export const comments = createQueryKeys("admin-news-comments", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  news: (newsId: number, page?: number, filter?: SearchRequest) => [
    { newsId, page, filter },
  ],
  replies: (commentId: number, page?: number) => [{ commentId, page }],
  one: (id?: number) => [id],
});

const include = "user,news";

export const fetchCommentsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...comments.all(page, search),
    queryFn: () =>
      adminApi
        .get<CommentList>(`/news/comments`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchComments = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCommentsQueryOptions(page, search));

export const fetchCommentReplies = (commentId: number, page: number = 0) =>
  queryOptions({
    ...comments.replies(commentId, page),
    queryFn: () =>
      adminApi
        .get<Paginate<AdminComment<true>>>(
          `/news/comments/${commentId}/replies`,
          {
            params: { page, include: "user" },
          },
        )
        .then((response) => response.data),
  });

export const useFetchCommentReplies = (commentId: number) =>
  usePaginateQuery((page) => fetchCommentReplies(commentId, page));

export const fetchCommentQueryOptions = (id?: number) =>
  queryOptions({
    ...comments.one(id),
    queryFn: () =>
      adminApi
        .get<AdminNews>(`/news/comments/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchComment = (id?: number) =>
  useQuery(fetchCommentQueryOptions(id));

export interface CommentRequest {
  content: string;
}

export interface CommentReplyRequest {
  content: string;
  status: "approved" | "pending" | "rejected";
}

export interface CommentStatusRequest {
  status: "approved" | "pending" | "rejected";
}

export const useUpdateComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CommentRequest }) =>
      adminApi
        .put(`/news/comments/${id}`, data)
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.one(id));
    },
  });
};

export const useUpdateCommentStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: number;
      status: "approved" | "pending" | "rejected";
    }) =>
      adminApi
        .patch(`/news/comments/${id}/status`, { status })
        .then((response) => response.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.one(id));
    },
  });
};
export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`news/comments/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.one(id));
    },
  });
};

export const useCreateCommentReply = (commentId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommentReplyRequest) =>
      adminApi
        .post<
          AdminComment<true, true>
        >(`/news/comments/${commentId}/replies?include=${include}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: comments.all._def,
      });
      queryClient.invalidateQueries(comments.replies(commentId));
    },
  });
};
